import { shiftWaveDate, type WavePlanningFixture, type WaveWeek } from './model'
import type { WavePlan } from './plans'
import {
  calculateSkuPlanning,
  type SkuWaveDrafts,
  type SkuWaveFixture,
  type SkuWaveLine,
} from './sku-model'

export interface RollupCalendar {
  id: string
  label: string
  year: number
  offsetDays: number
}

export interface RollupMetrics {
  percent: number | null
  units: number
  skuCount: number | null
  repackSkuCount: number | null
  cube: number | null
}

export type RollupMetric = keyof RollupMetrics
export type RollupChartMetric = 'percent' | 'units' | 'cube'
export interface RollupHighlight {
  planId: string
  week?: number
}

export interface RollupPlan {
  plan: WavePlan
  code: string
  fixture: SkuWaveFixture
  setDate: string
  baseUnits: number
  waves: Record<number, RollupMetrics>
  season: RollupMetrics
  activeWeeks: number[]
  waveCount: number
  needsReview: boolean
}

export interface RollupGroup {
  calendar: RollupCalendar
  plans: RollupPlan[]
  weeks: WaveWeek[]
  activeWeeks: WaveWeek[]
  waves: Record<number, RollupMetrics>
  baseUnits: number
  season: RollupMetrics
}

const ratio = (value: number, total: number) =>
  total > 0 ? value / total : null
const sumAvailable = (values: (number | null)[]) =>
  values.some(value => value === null || !Number.isFinite(value))
    ? null
    : values.reduce<number>((sum, value) => sum + (value ?? 0), 0)

function isRepack(line: SkuWaveLine) {
  return (
    (line.type === 'Single SKU' || line.type === 'Replen') &&
    Number.isFinite(Number(line.sku)) &&
    Number(line.sku) > 0 &&
    line.ip > 0 &&
    line.ip !== line.cp
  )
}

/** WAVE SUMMARY uses shipping lines per wave, sellable count for the season,
 * units excluding Parent, and total cube excluding Child (not cube per DC).
 * Keep the existing SKU allocation/whole-unit rounding as the single source.
 */
export function calculateWaveRollup({
  plans,
  calendars,
  fixtures,
  skuFixtures,
  weeks,
  drafts = {},
  skuDrafts = {},
}: {
  plans: WavePlan[]
  calendars: RollupCalendar[]
  fixtures: WavePlanningFixture[]
  skuFixtures: SkuWaveFixture[]
  weeks: WaveWeek[]
  drafts?: Record<string, number[]>
  skuDrafts?: Record<string, SkuWaveDrafts>
}): RollupGroup[] {
  return [...calendars]
    .sort((a, b) => a.year - b.year || a.id.localeCompare(b.id))
    .flatMap(calendar => {
      const timeline = weeks.map(week => ({
        ...week,
        date: shiftWaveDate(week.date, calendar.offsetDays),
      }))
      const eligibleWeeks = timeline.filter(week => week.event !== 'NO WAVE')
      const calendarPlans = [
        ...new Map(plans.map(plan => [plan.id, plan])).values(),
      ]
        .filter(plan => plan.calendarId === calendar.id)
        .map(plan => {
          const overview = fixtures.find(item => item.id === plan.subclassId)!
          const fixture = skuFixtures.find(item => item.id === plan.subclassId)!
          const key = `${calendar.id}:${plan.subclassId}`
          const result = calculateSkuPlanning(
            fixture,
            timeline,
            drafts[key] ?? overview.plannedWaves,
            skuDrafts[key]
          )
          const baseUnits = result.summary.totalQty
          const waveMetrics = Object.fromEntries(
            eligibleWeeks.map(week => {
              const units = result.rows
                .filter(row => row.line!.type !== 'Parent')
                .reduce((sum, row) => sum + row.waves[week.week].qty, 0)
              const cube = sumAvailable(
                result.rows
                  .filter(row => row.line!.type !== 'Child')
                  .map(row => {
                    const qty = row.waves[week.week].qty
                    if (qty === 0) return 0
                    return row.line!.cp > 0
                      ? (qty / row.line!.cp) * row.line!.cube
                      : null
                  })
              )
              return [
                week.week,
                {
                  units,
                  cube,
                  percent: ratio(units, baseUnits),
                  skuCount: result.rows.filter(
                    row =>
                      row.line!.type !== 'Child' &&
                      (row.waves[week.week].percent ?? 0) > 0
                  ).length,
                  repackSkuCount: result.rows.filter(
                    row =>
                      isRepack(row.line!) &&
                      (row.waves[week.week].percent ?? 0) > 0
                  ).length,
                },
              ]
            })
          )
          const metrics = Object.values(waveMetrics)
          const units = metrics.reduce((sum, wave) => sum + wave.units, 0)
          return {
            plan,
            fixture,
            code: overview.code,
            baseUnits,
            setDate: shiftWaveDate(fixture.setDate, calendar.offsetDays),
            waves: waveMetrics,
            activeWeeks: result.visibleWeeks.map(week => week.week),
            waveCount: metrics.filter(wave => wave.units > 0).length,
            needsReview: result.summary.needsReview,
            season: {
              units,
              percent: ratio(units, baseUnits),
              cube: sumAvailable(metrics.map(wave => wave.cube)),
              skuCount: fixture.sellableCount,
              repackSkuCount: null,
            },
          }
        })
      if (!calendarPlans.length) return []
      const baseUnits = calendarPlans.reduce(
        (sum, plan) => sum + plan.baseUnits,
        0
      )
      const total = (metrics: RollupMetrics[]): RollupMetrics => {
        const units = metrics.reduce((sum, item) => sum + item.units, 0)
        return {
          units,
          percent: ratio(units, baseUnits),
          cube: sumAvailable(metrics.map(item => item.cube)),
          skuCount: null,
          repackSkuCount: null,
        }
      }
      const active = new Set(calendarPlans.flatMap(plan => plan.activeWeeks))
      return [
        {
          calendar,
          plans: calendarPlans,
          weeks: eligibleWeeks,
          baseUnits,
          activeWeeks: eligibleWeeks.filter(week => active.has(week.week)),
          waves: Object.fromEntries(
            eligibleWeeks.map(week => [
              week.week,
              total(calendarPlans.map(plan => plan.waves[week.week])),
            ])
          ),
          season: total(calendarPlans.map(plan => plan.season)),
        },
      ]
    })
}

const numbers = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const percentages = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export function formatRollupValue(value: number | null, metric: RollupMetric) {
  if (value === null || !Number.isFinite(value)) return '—'
  return metric === 'percent'
    ? percentages.format(value)
    : numbers.format(value)
}

export function formatRollupDate(date: string) {
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year.slice(-2)}`
}
